import { LiliTextInput, Text } from '@lili/lili-frontend-common';
import { Controller } from 'react-hook-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { t as tGlobal } from 'i18next';
import { z } from 'zod';
import { normalizeNationalIdOnChange } from '@lili/utils/validation';
import eyeVisibleIcon from '@Assets/icons/eye-visible.svg';
import eyeIcon from '@Assets/icons/eye.svg';
import { OnboardingFormFields } from '@Types/types.ts';
import { useAvoidAutoComplete } from '@Hooks/custom/useAvoidAutoComplete.ts';
import type { Country } from '@Types/country.ts';

interface NationalIdInputProps {
  selectedCountry: Country | undefined;
  tabIndex?: number;
  helperText?: string;
  helperTextClassName?: string;
}

export const getConditionalNationalIdInputSchema = (isRequired: boolean) => {
  if (isRequired) {
    return z.object({
      [OnboardingFormFields.NATIONAL_ID_NUMBER]: z
        .string()
        .trim()
        .min(1, tGlobal('enErrors:required')),
    });
  }
  return z.object({
    [OnboardingFormFields.NATIONAL_ID_NUMBER]: z.string().optional(),
  });
};

export const getNationalIdSchemeByCountry = (country: Country | undefined) => {
  return z.object({
    [OnboardingFormFields.NATIONAL_ID_NUMBER]: z
      .string()
      .trim()
      .min(1, tGlobal('enErrors:required'))
      .regex(new RegExp(country?.idValidationRegex || ''), country?.idValidationDescription || ''),
  });
};

export const NationalIdInput = ({
  selectedCountry,
  tabIndex,
  helperText,
  helperTextClassName,
}: NationalIdInputProps) => {
  const { t } = useTranslation('enJSON');
  const [isNationalIdVisible, setIsNationalIdVisible] = useState(true);
  useAvoidAutoComplete({ idsToAvoidAutoComplete: [OnboardingFormFields.NATIONAL_ID_NUMBER] });

  const nationalIdHideSlot = () => {
    return (
      <button
        onClick={() => setIsNationalIdVisible((prevState) => !prevState)}
        className='z-50 absolute top-1/2 -translate-y-1/2 right-[12px] h-[24px] w-[24px]'>
        <img
          alt='hide'
          className='w-full object-cover'
          src={isNationalIdVisible ? eyeIcon : eyeVisibleIcon}
        />
      </button>
    );
  };

  return (
    <Controller
      name={OnboardingFormFields.NATIONAL_ID_NUMBER}
      render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => (
        <div>
          <LiliTextInput
            tabIndex={tabIndex}
            maxLength={19}
            value={value}
            title={selectedCountry?.nationalIdDescription || t('pages.nonUsIdentity.nationalId')}
            disabled={!selectedCountry}
            onChange={(newValue) => onChange(normalizeNationalIdOnChange(newValue))}
            onBlur={onBlur}
            showClear={false}
            id={OnboardingFormFields.NATIONAL_ID_NUMBER}
            accessoryItemComponent={nationalIdHideSlot()}
            type={isNationalIdVisible ? 'text' : 'password'}
            errorMessage={error?.message}
            isPublic={false}
            data-testid={OnboardingFormFields.NATIONAL_ID_NUMBER}
          />
          {helperText && !error && (
            <Text.H10 className={helperTextClassName}>{helperText}</Text.H10>
          )}
        </div>
      )}
    />
  );
};
